
import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { StartupAccount } from "@/types/startup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Building2, 
  Users, 
  Calendar, 
  BookOpen, 
  MessageSquare, 
  Star,
  TrendingUp,
  Crown,
  Settings,
  BarChart3
} from "lucide-react";
import StartupCommunities from "./StartupCommunities";
import StartupEvents from "./StartupEvents";
import StartupCourses from "./StartupCourses";
import StartupOpportunities from "./StartupOpportunities";
import StartupNewsSubmissions from "./StartupNewsSubmissions";
import StartupSettings from "./StartupSettings";
import FeaturingCard from "./FeaturingCard";

const StartupDashboard: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [startupAccount, setStartupAccount] = useState<StartupAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    communities: 0,
    events: 0,
    courses: 0,
    opportunities: 0
  });

  useEffect(() => {
    fetchStartupAccount();
    fetchStats();
  }, [user]);

  const fetchStartupAccount = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("startup_accounts")
        .select("*")
        .eq("user_id", user.id)
        .eq("status", "approved")
        .single();

      if (error) {
        console.error("Error fetching startup account:", error);
        return;
      }

      setStartupAccount(data);
    } catch (error) {
      console.error("Error in fetchStartupAccount:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!user) return;

    try {
      const { data: startupData } = await supabase
        .from("startup_accounts")
        .select("id")
        .eq("user_id", user.id)
        .eq("status", "approved")
        .single();

      if (!startupData) return;

      // Fetch all stats in parallel
      const [
        { count: communitiesCount },
        { count: eventsCount },
        { count: coursesCount },
        { count: opportunitiesCount }
      ] = await Promise.all([
        supabase.from("startup_communities").select("*", { count: "exact", head: true }).eq("startup_account_id", startupData.id),
        supabase.from("startup_events").select("*", { count: "exact", head: true }).eq("startup_account_id", startupData.id),
        supabase.from("startup_courses").select("*", { count: "exact", head: true }).eq("startup_account_id", startupData.id),
        supabase.from("startup_opportunities").select("*", { count: "exact", head: true }).eq("startup_account_id", startupData.id)
      ]);

      setStats({
        communities: communitiesCount || 0,
        events: eventsCount || 0,
        courses: coursesCount || 0,
        opportunities: opportunitiesCount || 0
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!startupAccount) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>No Startup Account Found</CardTitle>
            <CardDescription>
              You don't have an approved startup account yet. Please apply for one or wait for approval.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#FF8C00]">{startupAccount.startup_name}</h1>
          <p className="text-muted-foreground">Startup Dashboard</p>
        </div>
        <Badge variant="outline" className="border-[#FF8C00] text-[#FF8C00]">
          <Building2 className="h-4 w-4 mr-1" />
          {startupAccount.status}
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-[#FF8C00]/20 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Communities</p>
                <p className="text-2xl font-bold text-[#FF8C00]">{stats.communities}</p>
              </div>
              <Users className="h-8 w-8 text-[#FF8C00]" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#FF8C00]/20 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Events</p>
                <p className="text-2xl font-bold text-[#FF8C00]">{stats.events}</p>
              </div>
              <Calendar className="h-8 w-8 text-[#FF8C00]" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#FF8C00]/20 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Courses</p>
                <p className="text-2xl font-bold text-[#FF8C00]">{stats.courses}</p>
              </div>
              <BookOpen className="h-8 w-8 text-[#FF8C00]" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#FF8C00]/20 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Opportunities</p>
                <p className="text-2xl font-bold text-[#FF8C00]">{stats.opportunities}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-[#FF8C00]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="communities" className="w-full">
        <TabsList className="grid w-full grid-cols-7 bg-muted/50">
          <TabsTrigger value="communities" className="data-[state=active]:bg-[#FF8C00] data-[state=active]:text-white">
            <Users className="h-4 w-4 mr-2" />
            Communities
          </TabsTrigger>
          <TabsTrigger value="events" className="data-[state=active]:bg-[#FF8C00] data-[state=active]:text-white">
            <Calendar className="h-4 w-4 mr-2" />
            Events
          </TabsTrigger>
          <TabsTrigger value="courses" className="data-[state=active]:bg-[#FF8C00] data-[state=active]:text-white">
            <BookOpen className="h-4 w-4 mr-2" />
            Courses
          </TabsTrigger>
          <TabsTrigger value="opportunities" className="data-[state=active]:bg-[#FF8C00] data-[state=active]:text-white">
            <TrendingUp className="h-4 w-4 mr-2" />
            Opportunities
          </TabsTrigger>
          <TabsTrigger value="featuring" className="data-[state=active]:bg-[#FF8C00] data-[state=active]:text-white">
            <Crown className="h-4 w-4 mr-2" />
            Get Featured
          </TabsTrigger>
          <TabsTrigger value="news" className="data-[state=active]:bg-[#FF8C00] data-[state=active]:text-white">
            <MessageSquare className="h-4 w-4 mr-2" />
            News
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-[#FF8C00] data-[state=active]:text-white">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="communities" className="mt-6">
          <StartupCommunities startupAccount={startupAccount} />
        </TabsContent>

        <TabsContent value="events" className="mt-6">
          <StartupEvents startupAccount={startupAccount} />
        </TabsContent>

        <TabsContent value="courses" className="mt-6">
          <StartupCourses startupAccount={startupAccount} />
        </TabsContent>

        <TabsContent value="opportunities" className="mt-6">
          <StartupOpportunities startupAccount={startupAccount} />
        </TabsContent>

        <TabsContent value="featuring" className="mt-6">
          <FeaturingCard />
        </TabsContent>

        <TabsContent value="news" className="mt-6">
          <StartupNewsSubmissions startupAccount={startupAccount} />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <StartupSettings startupAccount={startupAccount} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StartupDashboard;
